import React, { useState, useEffect, useCallback } from 'react';

/**
 * AdminUsers — User management panel.
 *
 * Features:
 *   - List all registered users
 *   - Ban / unban users
 *   - Manual balance correction
 *   - View user details
 */
const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adjustUserId, setAdjustUserId] = useState(null);
    const [adjustAmount, setAdjustAmount] = useState('');
    const [adjustReason, setAdjustReason] = useState('');
    const [message, setMessage] = useState('');
    const [resetPasswords, setResetPasswords] = useState({}); // userId -> password
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newDisplayName, setNewDisplayName] = useState('');
    const [newBalance, setNewBalance] = useState('');
    const [creating, setCreating] = useState(false);

    const apiFetch = useCallback(async (path, opts = {}) => {
        const res = await fetch(`/api/admin${path}`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            ...opts,
        });
        return res.json();
    }, []);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/users');
            setUsers(data.users || []);
        } catch { /* ignore */ }
        setLoading(false);
    }, [apiFetch]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleBan = async (userId, isBanned) => {
        const action = isBanned ? 'unban' : 'ban';
        const reason = !isBanned ? window.prompt(`Reason for banning user ${userId}:`) : '';
        if (!isBanned && reason === null) return; // cancelled

        await apiFetch(`/user/${userId}/${action}`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
        });
        setMessage(`User ${userId} ${action}ned`);
        fetchUsers();
    };

    const handleBalanceAdjust = async (userId) => {
        const amount = parseFloat(adjustAmount);
        if (isNaN(amount) || amount === 0) return;
        if (!adjustReason.trim()) {
            setMessage('Reason is required for balance adjustments');
            return;
        }

        const data = await apiFetch(`/user/${userId}/balance`, {
            method: 'POST',
            body: JSON.stringify({ adjustmentSol: amount, reason: adjustReason }),
        });

        if (data.success) {
            setMessage(`Balance adjusted: ${amount > 0 ? '+' : ''}${amount} SOL`);
            setAdjustUserId(null);
            setAdjustAmount('');
            setAdjustReason('');
            fetchUsers();
        } else {
            setMessage(data.error || 'Failed');
        }
    };

    const handleResetPassword = async (userId, userName) => {
        if (!window.confirm(`Reset password for ${userName || 'User #' + userId}?`)) return;
        const data = await apiFetch(`/user/${userId}/reset-password`, { method: 'POST' });
        if (data.success) {
            setResetPasswords(prev => ({ ...prev, [userId]: data.password }));
            setMessage(`Password reset for ${userName || 'User #' + userId}: ${data.password}`);
        } else {
            setMessage(data.error || 'Failed to reset password');
        }
    };

    const handleCreateAccount = async () => {
        if (!newEmail.trim() || !newPassword.trim()) {
            setMessage('Email and password are required');
            return;
        }
        if (newPassword.length < 8) {
            setMessage('Password must be at least 8 characters');
            return;
        }
        setCreating(true);
        try {
            const balanceCredits = parseFloat(newBalance || '0');
            const balanceSol = balanceCredits > 0 ? balanceCredits / 100 : 0;
            const data = await apiFetch('/user/create', {
                method: 'POST',
                body: JSON.stringify({
                    email: newEmail.trim(),
                    password: newPassword,
                    displayName: newDisplayName.trim() || undefined,
                    initialBalance: balanceSol || undefined,
                }),
            });
            if (data.success) {
                setMessage(`Account created: ${data.user.email} (ID: ${data.user.id})`);
                setNewEmail('');
                setNewPassword('');
                setNewDisplayName('');
                setNewBalance('');
                setShowCreateForm(false);
                fetchUsers();
            } else {
                setMessage(data.error || 'Failed to create account');
            }
        } catch (err) {
            setMessage(err.message || 'Failed to create account');
        }
        setCreating(false);
    };

    const inputClass = 'bg-white/[0.03] border border-white/10 rounded px-3 py-1.5 text-xs text-white placeholder-gray-600 outline-none focus:border-blue-500/50 font-mono';

    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02]">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
                <h3 className="text-sm font-black uppercase tracking-[0.2em] text-gray-400">
                    User Accounts ({users.length})
                </h3>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded font-bold transition-colors"
                    >
                        {showCreateForm ? '✕ Cancel' : '+ Create Account'}
                    </button>
                    <button onClick={fetchUsers} className="text-xs text-blue-400 hover:text-blue-300">
                        Refresh
                    </button>
                </div>
            </div>

            {showCreateForm && (
                <div className="p-4 border-b border-emerald-500/20 bg-emerald-500/5">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Email *</label>
                            <input
                                type="email"
                                value={newEmail}
                                onChange={e => setNewEmail(e.target.value)}
                                placeholder="user@example.com"
                                className={`${inputClass} w-52`}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Password *</label>
                            <input
                                type="text"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder="min 8 characters"
                                className={`${inputClass} w-40`}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Display Name</label>
                            <input
                                type="text"
                                value={newDisplayName}
                                onChange={e => setNewDisplayName(e.target.value)}
                                placeholder="optional"
                                className={`${inputClass} w-36`}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Initial Credits</label>
                            <input
                                type="number"
                                step="0.01"
                                value={newBalance}
                                onChange={e => setNewBalance(e.target.value)}
                                placeholder="0.00"
                                className={`${inputClass} w-28`}
                            />
                        </div>
                        <button
                            onClick={handleCreateAccount}
                            disabled={creating}
                            className="text-xs px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded font-bold transition-colors"
                        >
                            {creating ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </div>
            )}

            {message && (
                <div className="px-4 py-2 bg-blue-500/10 border-b border-blue-500/20">
                    <p className="text-sm text-blue-400 font-mono">{message}</p>
                </div>
            )}

            {loading ? (
                <p className="text-sm text-gray-600 p-4">Loading users...</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/5">
                                <th className="text-left px-4 py-2">ID</th>
                                <th className="text-left px-4 py-2">Email</th>
                                <th className="text-center px-4 py-2">Password</th>
                                <th className="text-left px-4 py-2">Wallet</th>
                                <th className="text-left px-4 py-2">Name</th>
                                <th className="text-right px-4 py-2">Balance</th>
                                <th className="text-center px-4 py-2">Status</th>
                                <th className="text-right px-4 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <React.Fragment key={u.id}>
                                    <tr className="border-b border-white/5 hover:bg-white/[0.02]">
                                        <td className="px-4 py-2 font-mono text-gray-400">{u.id}</td>
                                        <td className="px-4 py-2 text-cyan-400 text-xs tracking-wide">
                                            {u.email || '—'}
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <div className="flex items-center justify-center gap-1.5">
                                                {resetPasswords[u.id] ? (
                                                    <span className="text-amber-400 font-mono text-[10px] select-all" title="Click to copy">
                                                        {resetPasswords[u.id]}
                                                    </span>
                                                ) : u.has_password ? (
                                                    <span className="text-emerald-400 font-bold" title="Password set">✓</span>
                                                ) : (
                                                    <span className="text-red-400 font-bold" title="No password">✗</span>
                                                )}
                                                <button
                                                    onClick={() => handleResetPassword(u.id, u.display_name || u.email)}
                                                    className="text-[9px] text-gray-500 hover:text-amber-400 transition-colors ml-1"
                                                    title="Reset password"
                                                >
                                                    ↻
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 font-mono text-gray-300 max-w-[120px] truncate">
                                            {u.wallet_address?.slice(0, 12)}...
                                        </td>
                                        <td className="px-4 py-2 text-white">
                                            {u.display_name || '—'}
                                            {u.is_admin && <span className="ml-1 text-[10px] text-amber-400 font-bold">ADMIN</span>}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-white">
                                            {(parseFloat(u.balance_sol) * 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-gray-600 text-[10px]">credits</span>
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <span className={`text-xs px-2 py-0.5 rounded font-bold uppercase ${u.is_banned ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'
                                                }`}>
                                                {u.is_banned ? 'BANNED' : 'ACTIVE'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => setAdjustUserId(adjustUserId === u.id ? null : u.id)}
                                                    className="text-xs text-blue-400 hover:text-blue-300"
                                                >
                                                    Adjust
                                                </button>
                                                <button
                                                    onClick={() => handleBan(u.id, u.is_banned)}
                                                    className={`text-xs ${u.is_banned ? 'text-emerald-400 hover:text-emerald-300' : 'text-red-400 hover:text-red-300'}`}
                                                >
                                                    {u.is_banned ? 'Unban' : 'Ban'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {adjustUserId === u.id && (
                                        <tr className="bg-blue-500/5">
                                            <td colSpan={8} className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="number"
                                                        step="0.001"
                                                        value={adjustAmount}
                                                        onChange={e => setAdjustAmount(e.target.value)}
                                                        placeholder="Amount SOL (+/-)"
                                                        className={`${inputClass} w-32`}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={adjustReason}
                                                        onChange={e => setAdjustReason(e.target.value)}
                                                        placeholder="Reason (required)"
                                                        className={`${inputClass} flex-1`}
                                                    />
                                                    <button
                                                        onClick={() => handleBalanceAdjust(u.id)}
                                                        className="text-[9px] px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded font-bold"
                                                    >
                                                        Apply
                                                    </button>
                                                    <button
                                                        onClick={() => setAdjustUserId(null)}
                                                        className="text-[9px] text-gray-500 hover:text-gray-300"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;
